/* UploadInvoiceDrawer — PS.openUploadDrawer port. Vertical + partner + period +
   FileDrop (with template), pushes a new "No Dispute / Uploaded" invoice onto
   DUMMY_INVOICES (in-memory). */
import React, {useState} from 'react'
import PropTypes from 'prop-types'
import CheckIcon from '@mui/icons-material/CheckOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined'
import {SideDrawer, FileDrop, SearchSelect} from '../components'
import {DUMMY_INVOICES, DUMMY_PARTNERS, TODAY} from '../dummyData'
import {ME} from './reconShared'

const TEMPLATE_NAME = 'courier-invoice-template.csv'
const TEMPLATE = [
  'partner,vertical,invoice_period,total_orders,total_amount,remark',
  'Delhivery,D2C,01-31 May 2026,12890,1902400,May D2C surface',
  'Xpressbees,D2C,01-31 May 2026,3340,2380500,May D2C',
].join('\n')

export default function UploadInvoiceDrawer({open, verticals, onClose, onUploaded, toast}) {
  const [vertical, setVertical] = useState(verticals[0] || 'D2C')
  const [partner, setPartner] = useState('')
  const [period, setPeriod] = useState('')
  const [fileName, setFileName] = useState(null)
  const [err, setErr] = useState({})

  // reset on open
  React.useEffect(() => {
    if (open) {
      setVertical(verticals[0] || 'D2C')
      setPartner('')
      setPeriod('')
      setFileName(null)
      setErr({})
    }
  }, [open, verticals])

  const submit = () => {
    const e = {}
    if (!partner) e.partner = true
    if (!period.trim()) e.period = true
    if (Object.keys(e).length) {
      setErr(e)
      toast(!partner ? 'Select a partner.' : 'Enter the invoice period.', 'error')
      return
    }
    if (!fileName) {
      toast('Attach an invoice file first.', 'error')
      return
    }
    const pid = (DUMMY_PARTNERS.find((p) => p.name === partner) || {}).id || partner.toLowerCase()
    const billed = 4282900
    const expected = Math.round(billed / 1.03)
    const variance = billed - expected
    DUMMY_INVOICES.unshift({
      id:
        'INV-' +
        TODAY.toISOString().slice(2, 7).replace('-', '') +
        '-' +
        partner.slice(0, 2).toUpperCase() +
        'X',
      partner,
      partnerId: pid,
      vertical,
      monthKey: TODAY.toISOString().slice(0, 7),
      period: period.trim(),
      orders: 16230,
      billedAmount: billed,
      amount: billed,
      expectedAmount: expected,
      variance,
      disputedAmount: 0,
      cnAmount: 0,
      reconStatus: 'No Dispute',
      reason: null,
      owner: null,
      createdBy: ME,
      createdAt: TODAY.toISOString(),
      raisedDate: null,
      resolvedDate: null,
      resolutionDays: null,
      status: 'Uploaded',
      remark: 'Uploaded ' + period.trim(),
      history: [],
    })
    onClose()
    toast(
      'Invoice uploaded for ' + partner + ' (' + vertical + '). Reconciliation pending.',
      'success'
    )
    onUploaded()
  }

  const footer = (
    <>
      <button className='btn btn-ghost' onClick={onClose}>
        Cancel
      </button>
      <button className='btn btn-primary' onClick={submit}>
        <CheckIcon style={{width: 16, height: 16}} />
        Upload Invoice
      </button>
    </>
  )

  return (
    <SideDrawer
      open={open}
      onClose={onClose}
      width={560}
      title='Upload Courier Invoice'
      footer={footer}
    >
      <div className='field'>
        <label>Freight vertical</label>
        {verticals.length > 1 ? (
          <SearchSelect
            value={vertical}
            onChange={(v) => setVertical(v)}
            options={verticals.map((v) => ({value: v, label: v + ' Freight'}))}
            placeholder='Search…'
          />
        ) : (
          <div>
            <span className={'chip chip-' + String(vertical).toLowerCase()}>
              {vertical} Freight
            </span>
          </div>
        )}
        <div className='help'>
          <InfoOutlinedIcon />
          Which commercial module the invoice is filed under.
        </div>
      </div>

      <div className='field'>
        <label>
          Partner <span className='req'>*</span>
        </label>
        <SearchSelect
          value={partner}
          onChange={(v) => {
            setPartner(v)
            setErr((p) => ({...p, partner: false}))
          }}
          options={DUMMY_PARTNERS.map((p) => ({value: p.name, label: p.name}))}
          placeholder='Select partner…'
          invalid={!!err.partner}
        />
      </div>

      <div className='field'>
        <label>
          Invoice period <span className='req'>*</span>
        </label>
        <input
          className={'input' + (err.period ? ' err' : '')}
          placeholder='e.g. 01–31 May 2026'
          value={period}
          onChange={(e) => {
            setPeriod(e.target.value)
            setErr((p) => ({...p, period: false}))
          }}
        />
      </div>

      <div className='field'>
        <label>
          Invoice file <span className='req'>*</span>
        </label>
        <FileDrop
          accept='.csv,.xlsx'
          maxMB={10}
          title='Drag & drop the courier invoice'
          hint='Accepted: .csv, .xlsx · max 10 MB'
          sampleName={TEMPLATE_NAME}
          sampleLabel='Download template'
          sampleContent={TEMPLATE}
          onFile={(f) => setFileName(f.name)}
          onError={(m) => toast(m, 'error')}
        />
      </div>

      {fileName ? (
        <div className='warnbox ok' style={{marginTop: 12}}>
          <CheckCircleOutlinedIcon />
          <div>
            File parsed. Invoice rows ready to import; reconciliation will compute variance vs rate
            card (mock).
          </div>
        </div>
      ) : null}
    </SideDrawer>
  )
}

UploadInvoiceDrawer.propTypes = {
  open: PropTypes.bool,
  verticals: PropTypes.array,
  onClose: PropTypes.func,
  onUploaded: PropTypes.func,
  toast: PropTypes.func,
}
